import { z } from "zod";
import { completionRoundSchema } from "./completion-round";
import type { Locale } from "./common";
import {
  FORM_UPLOAD_MAX_BYTES,
  formUploadMetaSchema,
  type TemplateField,
} from "./intake-template";

// TES-590 — CP-06 "Komplettera ditt medicinska underlag"
// as ONE checklist of case-bound tasks the doctor switches on after the
// consult. Previous lab results and prescription evidence use the SAME
// secure upload component and storage (FormUpload) as F-07 did, but are two
// separate tasks with two separate categories, each with its own
// "Klar"/"Saknas" status. Nothing here shows the patient a lab value.

export const MEDICAL_EVIDENCE_TEMPLATE_KEY = "medical_evidence";

export const MEDICAL_EVIDENCE_CATEGORIES = [
  "PREVIOUS_LAB_RESULTS",
  "PRESCRIPTION_EVIDENCE",
] as const;
export type MedicalEvidenceCategory =
  (typeof MEDICAL_EVIDENCE_CATEGORIES)[number];

// FormUpload.fieldKey per category — the category IS the field.
export const MEDICAL_EVIDENCE_FIELD_KEY: Record<
  MedicalEvidenceCategory,
  string
> = {
  PREVIOUS_LAB_RESULTS: "previous_lab_results",
  PRESCRIPTION_EVIDENCE: "prescription_evidence",
};

export function medicalEvidenceCategoryOf(
  fieldKey: string,
): MedicalEvidenceCategory | null {
  for (const c of MEDICAL_EVIDENCE_CATEGORIES) {
    if (MEDICAL_EVIDENCE_FIELD_KEY[c] === fieldKey) return c;
  }
  return null;
}

const UPLOAD = {
  // TES-738 — a lab report or prescription is often a PDF; the server
  // accepts it. The app picks images only until a document picker ships
  // (native module + TestFlight), so this is the server side of that door.
  allowedMimeTypes: ["image/jpeg", "image/png", "application/pdf"],
  maxBytes: FORM_UPLOAD_MAX_BYTES,
  maxCount: 10,
};

// The two upload fields, shaped exactly like a published form template's
// file_upload fields so FormUploadsService and the app's FileUploadField
// treat them like any other (photo + screenshot, several images).
// The prescription copy is the verbatim; the previous-labs copy is ours
// (the package names the task, not its sentence — flagged in TES-590).
export const MEDICAL_EVIDENCE_FIELDS: TemplateField[] = [
  {
    key: MEDICAL_EVIDENCE_FIELD_KEY.PREVIOUS_LAB_RESULTS,
    kind: "file_upload",
    label: {
      sv: "Ladda upp tidigare provsvar",
      en: "Upload previous lab results",
    },
    help: {
      sv: "Ta ett foto eller välj en skärmbild av dina tidigare provsvar där ditt namn, provdatum och värdena syns så tydligt som möjligt.",
      en: "Take a photo or choose a screenshot of your previous lab results where your name, the sample date and the values are as clear as possible.",
    },
    required: false,
    evaluatorPinned: false,
    upload: UPLOAD,
  },
  {
    key: MEDICAL_EVIDENCE_FIELD_KEY.PRESCRIPTION_EVIDENCE,
    kind: "file_upload",
    label: {
      sv: "Ladda upp underlag om din nuvarande behandling",
      en: "Upload evidence of your current treatment",
    },
    help: {
      sv: "Läkaren behöver ett underlag som visar ditt aktuella recept eller din ordination. Ta ett foto eller välj en skärmbild där ditt namn, läkemedlets namn, styrka och receptets datum eller giltighet syns så tydligt som möjligt.",
      en: "The doctor needs evidence showing your current prescription or order. Take a photo or choose a screenshot where your name, the medication's name, strength and the prescription's date or validity are as clear as possible.",
    },
    required: false,
    evaluatorPinned: false,
    upload: UPLOAD,
  },
];

// TES-1000 (Ö-246) — a doctor's "Annan specificerad komplettering" that
// allows an upload stores its files under the medical-evidence template with
// a field key naming the task. The API checks the task is the patient's own
// and allows uploads before it accepts a file.
export const CUSTOM_TASK_FIELD_PREFIX = "custom_task.";

export function customTaskFieldKey(taskId: string): string {
  return `${CUSTOM_TASK_FIELD_PREFIX}${taskId}`;
}

export function customTaskIdOf(fieldKey: string): string | null {
  return fieldKey.startsWith(CUSTOM_TASK_FIELD_PREFIX)
    ? fieldKey.slice(CUSTOM_TASK_FIELD_PREFIX.length) || null
    : null;
}

export function medicalEvidenceField(fieldKey: string): TemplateField | null {
  const fixed = MEDICAL_EVIDENCE_FIELDS.find((f) => f.key === fieldKey);
  if (fixed) return fixed;
  if (customTaskIdOf(fieldKey) === null) return null;
  // The doctor's heading and instruction are the row's words; the field
  // itself only needs generic ones.
  return {
    key: fieldKey,
    kind: "file_upload",
    label: { sv: "Ladda upp fil", en: "Upload a file" },
    help: {
      sv: "Ta ett foto eller välj en skärmbild eller PDF som visar det läkaren har bett om.",
      en: "Take a photo or choose a screenshot or PDF showing what the doctor asked for.",
    },
    required: false,
    evaluatorPinned: false,
    upload: UPLOAD,
  };
}

// --- the checklist ----------------------------------------------------------

export const MEDICAL_EVIDENCE_TASK_KEYS = [
  "previous_lab_results",
  "prescription_evidence",
  // TES-720 — CP-06 variant B: "Betala blodprovet" is a row of its own,
  // before the "Lämna blodprovet" it unlocks. Only when the next step is a
  // new blood test; uploaded or already verified samples cost nothing.
  "lab_payment",
  "new_blood_test",
  "blood_pressure_pulse",
] as const;
export const medicalEvidenceTaskKeySchema = z.enum(MEDICAL_EVIDENCE_TASK_KEYS);
export type MedicalEvidenceTaskKey = z.infer<
  typeof medicalEvidenceTaskKeySchema
>;

export const medicalEvidenceTaskStatusSchema = z.enum(["done", "missing"]);
export type MedicalEvidenceTaskStatus = z.infer<
  typeof medicalEvidenceTaskStatusSchema
>;

export const medicalEvidenceTaskSchema = z.object({
  key: medicalEvidenceTaskKeySchema,
  status: medicalEvidenceTaskStatusSchema,
  // Upload tasks: how many files the patient has attached for this case.
  uploads: z.number().int().nonnegative(),
  // Ö-136 — `new_blood_test` only. True when the task is
  // done because the PATIENT reported giving the sample, and the results have
  // not arrived yet.
  //
  // It exists so no surface can quietly upgrade her word into a lab result:
  // her decision calls it a patient-reported fact
  // ("en patientrapporterad uppgift"), not a confirmation that the result has
  // been received or reviewed. The doctor's view needs to see which of the
  // two it is, and the patient's must not claim the stronger one.
  selfReported: z.boolean().optional(),
  // TES-720 — `new_blood_test` only. True while `lab_payment` is missing:
  // "Lämna blodprovet är beroende av verifierad betalning och är låst tills dess."
  // The server refuses the self-report while locked; the app shows the row
  // without its CTAs. Other tasks are never locked by the payment.
  locked: z.boolean().optional(),
  // TES-720 — `lab_payment` only. The price comes from versioned product
  // data (tenant config, snapshotted on the payment), never from copy.
  // `paidAt` is null until the payment is verified.
  payment: z
    .object({
      amountMinor: z.number().int().nonnegative(),
      currency: z.string(),
      paidAt: z.string().nullable(),
    })
    .optional(),
});
export type MedicalEvidenceTask = z.infer<typeof medicalEvidenceTaskSchema>;

// TES-736 — what is still open in CP-06 when the doctor is about to publish
// the treatment decision. Emelie 2026-09-16: blood test, blood pressure and
// resting pulse "visas som tydliga varningar", never as a block; the doctor
// confirms
// "Godkänn och publicera behandling med medicinsk uppföljning kvar" and the
// list goes into the audit row. The five task keys plus one that no task
// carries: a sample given (or a result landed) that no doctor has reviewed.
// TES-890 — the two outcomes that mean "reviewed, and not good enough" are
// their own items. Folding them back into `labs_not_reviewed` would have the
// decision dialog tell Kirill that nobody has looked at a result he looked at
// himself, which is the one thing a warning list must not do.
export const FOLLOW_UP_ITEMS = [
  ...MEDICAL_EVIDENCE_TASK_KEYS,
  "labs_not_reviewed",
  "labs_completion_required",
  "labs_new_sample_required",
] as const;
export const followUpItemSchema = z.enum(FOLLOW_UP_ITEMS);
export type FollowUpItem = z.infer<typeof followUpItemSchema>;

export const FOLLOW_UP_ITEM_LABELS: Record<
  FollowUpItem,
  { sv: string; en: string }
> = {
  previous_lab_results: {
    sv: "Tidigare provsvar är inte uppladdade",
    en: "Earlier lab results are not uploaded",
  },
  prescription_evidence: {
    sv: "Receptunderlaget är inte uppladdat",
    en: "Prescription evidence is not uploaded",
  },
  lab_payment: {
    sv: "Blodprovet är inte betalt",
    en: "The blood test is not paid",
  },
  new_blood_test: {
    sv: "Blodprovet är inte lämnat",
    en: "The blood sample has not been given",
  },
  blood_pressure_pulse: {
    sv: "Blodtryck och vilopuls saknas",
    en: "Blood pressure and resting pulse are missing",
  },
  labs_not_reviewed: {
    sv: "Provsvaret är inte granskat av läkare",
    en: "The lab result has not been reviewed by a doctor",
  },
  labs_completion_required: {
    sv: "Provsvaret är granskat — komplettering krävs",
    en: "The lab result is reviewed — a completion is required",
  },
  labs_new_sample_required: {
    sv: "Provsvaret är granskat — nytt prov krävs",
    en: "The lab result is reviewed — a new sample is required",
  },
};

// TES-953 (TES-941, the patient completion flow) — a prescription
// transfer komplettering that reopens F-07 sections, as ONE row of this
// checklist ("Komplettera uppgifter om din behandling"). It used to be a
// separate Home card and a separate page with the doctor's free text and a
// chat button, beside the checklist rather than in it.
//
// A field of its own rather than a sixth task key: the five keys are also the
// doctor's vocabulary (the admin's pickers, FOLLOW_UP_ITEMS), and this row is
// not something he picks — it is how a reopened F-07 is shown to her. It is
// sent only to clients that declare CLIENT_CONTRACT_CHANGES.unifiedCompletion;
// app 2.0.1 cannot render it and never receives it.
export const medicalEvidenceTransferCompletionSchema = z.object({
  // "missing" while the round is open: resubmitting F-07 returns the review to
  // the doctor's queue, and the row goes with it.
  status: medicalEvidenceTaskStatusSchema,
  // The requested F-07 section ids (`CompletionGroup` values that are F-07
  // sections). Plain strings so this file does not import the review contract.
  groups: z.array(z.string()),
});
export type MedicalEvidenceTransferCompletion = z.infer<
  typeof medicalEvidenceTransferCompletionSchema
>;

// TES-1000 (Ö-246 (b), Emelie 2026-09-29) — "Annan specificerad
// komplettering" as a row of this checklist:
// "Kirill anger patientvänlig rubrik och instruktion samt eventuell tillåten destination eller uppladdning."
// "Uppgiften ska: ingå i 0 av X klara, kunna slutföras, vara auditloggad,"
// "ligga i den samlade CP-06-checklistan."
//
// A list of its own rather than a sixth task key, for the reason TES-953 gave
// `transferCompletion`: the five keys are the doctor's vocabulary, and these
// rows carry his own words instead of fixed copy. Sent to the patient only
// when her client declares CLIENT_CONTRACT_CHANGES.customCompletionTask; an
// older app would count a row it cannot show.
//
// Done when she marks it done, or — when the doctor allowed an upload — when
// she has attached a file.
// TES-1149 — what a doctor's own row asks of her: something to do, or a
// question to answer in words.
export const CUSTOM_TASK_KINDS = ["task", "question"] as const;
export type CustomTaskKind = (typeof CUSTOM_TASK_KINDS)[number];

// TES-1149 — her written answer to a doctor's question. The same ceiling as
// the doctor's instruction.
export const DOCTOR_QUESTION_ANSWER_MAX = 2000;
export const doctorQuestionAnswerRequestSchema = z.object({
  text: z.string().trim().min(1).max(DOCTOR_QUESTION_ANSWER_MAX),
});
export type DoctorQuestionAnswerRequest = z.infer<
  typeof doctorQuestionAnswerRequestSchema
>;

export const medicalEvidenceCustomTaskSchema = z.object({
  id: z.string().uuid(),
  heading: z.string(),
  instruction: z.string(),
  uploadAllowed: z.boolean(),
  // The upload field key for this row (`customTaskFieldKey(id)`), when an
  // upload is allowed.
  fieldKey: z.string().nullable(),
  status: medicalEvidenceTaskStatusSchema,
  uploads: z.number().int().nonnegative(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  // TES-1149 — "question" is a doctor's question she answers in words
  // (`instruction` is the question): done once she has answered, never by
  // "Markera som klar" or by an upload alone. Optional so an older server's
  // response still parses; absent means "task". Sent as a question only to
  // clients that declare CLIENT_CONTRACT_CHANGES.doctorQuestionTask; older
  // clients get the row as a plain task whose instruction asks them to
  // update the app (doctor-question.ts).
  kind: z.enum(CUSTOM_TASK_KINDS).optional(),
  // TES-1149 — her answer, once sent. Read only: one answer per question.
  answer: z
    .object({ text: z.string(), answeredAt: z.string() })
    .nullable()
    .optional(),
});
export type MedicalEvidenceCustomTask = z.infer<
  typeof medicalEvidenceCustomTaskSchema
>;

// GET /patient-api/v1/medical-evidence — what CP-06 asks of her right now.
// `active` is false before the doctor has documented a consult with any
// requirement. TES-736: it is NOT closed by the published treatment decision
// (CP-09) any more — Emelie 2026-09-16, translated: CP-09, CP-11, CP-12,
// treatment_activated and first_dose_reported must not close CP-06. The
// case closes when the patient's tasks are all done AND the follow-up has
// been medically reviewed (a lab task: the doctor reviewed the order; no lab
// task: the decision is published).
export const medicalEvidenceChecklistSchema = z.object({
  active: z.boolean(),
  // When the case opened. Named for the consult that opened it historically;
  // since TES-741 a transfer review's "nytt blodprov krävs" opens a case too,
  // and this then carries `labsRequestedAt`. Kept under this name because
  // the app and the admin read it as the upload cutoff.
  meetingDocumentedAt: z.string().nullable(),
  // TES-741 — which kind of case this is. Optional so an older client parses.
  // TES-906 adds `lab_review`: a reviewed sample the doctor rejected opens a
  // case of its own, dated to the review rather than to the consult.
  // TES-978 adds `cycle_review`: a komplettering asked at the monthly review.
  // The app (2.0.1/2.0.2) does not read this field, so a new value needs no
  // app build.
  // TES-1059 adds `measurement_review`: "Behöver ny mätning" on a reading,
  // when no other case is open to carry the new reading.
  caseKind: z
    .enum([
      "meeting",
      "transfer_review",
      "lab_review",
      "cycle_review",
      "measurement_review",
      // TES-1169 — "Begär komplettering" from the patient overview, made
      // while no other case was open.
      "completion_request",
    ])
    .optional(),
  tasks: z.array(medicalEvidenceTaskSchema),
  allDone: z.boolean(),
  // TES-953 — the F-07 row, counted with the tasks. Absent for older clients
  // and whenever no transfer komplettering has reopened F-07.
  transferCompletion: medicalEvidenceTransferCompletionSchema.optional(),
  // TES-1000 — the doctor's own rows, counted with the tasks. Absent for
  // older clients and when there are none.
  customTasks: z.array(medicalEvidenceCustomTaskSchema).optional(),
  // TES-1059 — "n av m klara" from the stored completion round, when the
  // case has one and the client is shown every row the round counts.
  // Optional: an older client counts the rows itself, as before.
  progress: z
    .object({ done: z.number().int(), total: z.number().int() })
    .optional(),
});
export type MedicalEvidenceChecklist = z.infer<
  typeof medicalEvidenceChecklistSchema
>;

// Clinician view: the same checklist plus the attached files, category by
// category, so the doctor sees the uploads in the SAME case.
export const medicalEvidenceUploadSchema = formUploadMetaSchema.extend({
  category: z.enum(MEDICAL_EVIDENCE_CATEGORIES).nullable(),
});
export type MedicalEvidenceUpload = z.infer<typeof medicalEvidenceUploadSchema>;

// TES-942 — ONE blood-test status per case, derived on the server from the
// case window, the case's verified payment, the order and her self-report
// (`deriveBloodTestStatus` in the api). Admin used to print every LabOrder's
// raw `status` while the app read the checklist, so the same patient could be
// "resulted" in one and "not paid, not given" in the other (TES-941
// §STATUSKONSISTENS).
//
// There is deliberately no automatic "results received" state: without a
// Webdoc integration the system cannot know that a result has arrived.
// `result_registered` exists only because a doctor pressed "Provsvar har
// kommit in" on an order that belongs to this case and is paid for.
export const BLOOD_TEST_STATUSES = [
  "awaiting_payment",
  "awaiting_sample",
  "sample_reported",
  "result_registered",
  "reviewed",
] as const;
export const bloodTestStatusSchema = z.enum(BLOOD_TEST_STATUSES);
export type BloodTestStatus = z.infer<typeof bloodTestStatusSchema>;

export const BLOOD_TEST_STATUS_LABELS: Record<BloodTestStatus, string> = {
  awaiting_payment: "Väntar på betalning",
  awaiting_sample: "Betalt – väntar på provtagning",
  sample_reported: "Patienten har lämnat provet – inväntar provsvar",
  result_registered: "Provsvar markerat som inkommet – inväntar granskning",
  reviewed: "Provsvaret är granskat",
};

// The clinician's view of the current case's blood test. Null when the case
// has no blood-test task (or there is no case).
export const clinicianBloodTestSchema = z.object({
  status: bloodTestStatusSchema,
  // False when the doctor's case includes the sample without a payment task
  // (a covered re-test, a targeted komplettering).
  paymentRequired: z.boolean(),
  paidAt: z.string().nullable(),
  // The order that belongs to this case, if any. An order placed before the
  // case opened is never this one — it is listed as "Tidigare".
  orderId: z.string().nullable(),
  sampleReportedAt: z.string().nullable(),
  resultsReceivedAt: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  reviewOutcome: z.string().nullable(),
});
export type ClinicianBloodTest = z.infer<typeof clinicianBloodTestSchema>;

export const clinicianMedicalEvidenceSchema =
  medicalEvidenceChecklistSchema.extend({
    uploads: z.array(medicalEvidenceUploadSchema),
    // Optional so an admin built before TES-942 keeps parsing.
    bloodTest: clinicianBloodTestSchema.nullable().optional(),
    // TES-1059 — the patient's completion rounds, newest first: the current
    // one's progress and every item with its source and time. Optional so
    // an older admin parses.
    rounds: z.array(completionRoundSchema).optional(),
  });
export type ClinicianMedicalEvidence = z.infer<
  typeof clinicianMedicalEvidenceSchema
>;

// --- patient copy -----------------------------------------------------------
// Checklist title/body and the blood-test / blood-pressure tasks are the
// CP-06 packages' own words (the QA content); the prescription task is
// the package's copy verbatim; the previous-labs body is ours (flagged).
export const MEDICAL_EVIDENCE_COPY: Record<
  Locale,
  {
    title: string;
    body: string;
    cta: string;
    // TES-1169 — the intro's "När detta är klart" line, under the tasks.
    whenDone: { label: string; body: string };
    // Ö-136 — CP-06's NEUTRAL WAITING STATE, when the patient's verifiable
    // tasks are done.
    //
    // She decided at the same time that CP-08 remains only as a technical
    // legacy alias and must not be rendered as a patient-facing package: the
    // `medical_evidence_completed` event therefore does NOT have CP-08's
    // waiting card as its target — it brings CP-06 here instead.
    //
    // What it must not say, per her decision: that the lab results have
    // arrived, that the documentation is complete, or that the doctor has
    // started the review. The app knows none of that. It only knows that the
    // patient has done her part.
    //
    // No primary CTA: there is nothing for her to do.
    waiting: { title: string; body: string };
    // TES-942 — the same waiting state when the case includes a new blood
    // test and her part is done. Emelie 2026-09-25 (TES-941), verbatim. It
    // says the result is awaited and that the doctor comes back once it has
    // arrived — never that it is reviewed automatically or already reviewed.
    // TES-1094 — replaced by Emelie's canonical waiting copy (top of CP-06 in
    // Drive, 2026-10-04), verbatim.
    labWaiting: { title: string; body: string };
    // TES-953 — the transfer komplettering's F-07 row. The requested parts
    // are MANDATORY here, unlike the first F-07 where blood pressure, pulse
    // and earlier lab results are voluntary (TES-941, from F-02B to F-07).
    transferCompletion: { title: string; body: string; cta: string };
    // TES-1000 — the doctor's own row: its title and body are his; only the
    // action is fixed copy (ours, flagged in the PR).
    // TES-1149 — a doctor's question: the answer field, its button, the
    // line under a sent answer ("{{date}}" is filled in by the app), and the
    // line an app too old to answer gets in place of the field. Proposed
    // copy, pending Emelie.
    customTask: {
      markDoneCta: string;
      answerLabel: string;
      answerCta: string;
      answeredOn: string;
      updateAppToAnswer: string;
      // TES-1380 (TES-1376 §3B) — a row with files is sent, not completed by
      // the upload: the button once a file is attached, the line under an
      // attached but unsent file, and the line once sent. `submitted` is
      // CP-06's own line ("Läge 7", "Efter registrering"), verbatim; the other
      // two are ours, pending Emelie.
      submitCta: string;
      uploadedNotSubmitted: string;
      submitted: string;
      // TES-1635 — the action on the row's own Hem card: one that takes an
      // upload says so in the upload rows' words ("Ladda upp underlag",
      // prescription_evidence's CTA); one that does not gets "Gör uppgiften".
      // The latter is ours, pending Emelie.
      homeCta: string;
      homeUploadCta: string;
    };
    status: Record<MedicalEvidenceTaskStatus, string>;
    tasks: Record<
      MedicalEvidenceTaskKey,
      {
        title: string;
        body: string;
        cta: string;
        // Ö-136 — `new_blood_test` only. Her own "jag har lämnat provet", and
        // the line shown once she has. Both mirror her decision rather than
        // framing it anew: when a blood test is included, the patient must
        // herself be able to mark that she has given the sample; it is a
        // patient-reported fact, not a confirmation that the result has been
        // received or reviewed.
        reportCta?: string;
        reportedNote?: string;
        // TES-720 — `lab_payment` only: the row reads "Betalt"/"Att göra"
        // rather than "Klar"/"Saknas", in the document's words.
        statusLabels?: Record<MedicalEvidenceTaskStatus, string>;
        // TES-720 — `new_blood_test` only: what the locked row says.
        lockedNote?: string;
        // TES-737 — `new_blood_test` only: undo her own report while the
        // case is open (the patient must be able to undo a mistaken mark as
        // long as CP-06 is still active).
        undoReportCta?: string;
      }
    >;
  }
> = {
  sv: {
    // TES-1169 (Emelie 2026-10-08, CP-06 "ÖVERORDNAT TILLÄGG — SAMMA CP-06
    // ÄVEN VID SENARE KOMPLETTERING") — the time-neutral standard copy,
    // verbatim. A komplettering may now be asked during the consult, right
    // after it or months into treatment, so the standard copy must not assume
    // a consult just happened. It replaces TES-953's "Komplettera ditt
    // medicinska underlag" / "För att läkaren ska kunna gå vidare med din
    // behandling …", which spoke of the treatment decision only. The same
    // line on Hem's card, at the top of the checklist, and as the transfer
    // review's stored patient message (COMPLETION_STANDARD_PATIENT_TEXT).
    title: "Vi behöver en komplettering från dig",
    body: "Läkaren behöver ytterligare information för att kunna fortsätta din medicinska bedömning eller uppföljning. Se och slutför uppgiften nedan.",
    whenDone: {
      label: "När detta är klart",
      body: "Läkaren kan gå vidare med ditt ärende.",
    },
    // TES-720 — the document's card CTA ("Se och slutför uppgifterna").
    cta: "Se och slutför uppgifterna",
    waiting: {
      title: "Du har gjort dina uppgifter",
      body: "Du behöver inte göra något mer i Ella just nu. Du får besked i appen när nästa steg är klart.",
    },
    labWaiting: {
      title: "Ditt underlag är inskickat",
      body: "Du behöver inte göra något mer i Ella just nu. Vi inväntar provsvaret. Läkaren återkommer när hela underlaget kan bedömas.",
    },
    // TES-953 — the title is the ticket's; body and CTA are ours (flagged in
    // the PR), saying the parts are required rather than optional.
    transferCompletion: {
      title: "Komplettera uppgifter om din behandling",
      body: "Läkaren behöver att du fyller i de här delarna av uppgifterna om din nuvarande behandling. De är obligatoriska för att läkaren ska kunna gå vidare.",
      cta: "Komplettera uppgifterna",
    },
    customTask: {
      markDoneCta: "Markera som klar",
      answerLabel: "Ditt svar",
      answerCta: "Skicka svar",
      answeredOn: "Skickat {{date}}",
      updateAppToAnswer:
        "Uppdatera appen till den senaste versionen för att kunna svara på frågan.",
      submitCta: "Skicka in till läkaren",
      uploadedNotSubmitted:
        "Uppladdat men inte inskickat. Läkaren får underlaget när du har skickat in det.",
      // Ö-597 (Emelie 2026-10-20), verbatim: it says the doctor has it, and
      // promises no assessment — sent is not medically reviewed.
      submitted: "Underlaget är inskickat till läkaren.",
      homeCta: "Gör uppgiften",
      homeUploadCta: "Ladda upp underlag",
    },
    status: { done: "Klar", missing: "Saknas" },
    tasks: {
      // Ö-112 — her canonical body copy, verbatim.
      // It swaps a longer instruction
      // ("där ditt namn, provdatum och värdena syns…") for a shorter line
      // that instead says what happens next. That is her choice: the
      // practical guidance remains in the upload field, while the card
      // answers the question the patient actually has — what am I waiting
      // for?
      previous_lab_results: {
        title: "Ladda upp tidigare provsvar",
        body: "Ladda upp tydliga bilder av dina befintliga provsvar. Läkarens bedömning fortsätter när det obligatoriska underlaget är komplett.",
        cta: "Ladda upp provsvar",
      },
      prescription_evidence: {
        title: "Ladda upp underlag om din nuvarande behandling",
        body: "Läkaren behöver ett underlag som visar ditt aktuella recept eller din ordination. Ta ett foto eller välj en skärmbild där ditt namn, läkemedlets namn, styrka och receptets datum eller giltighet syns så tydligt som möjligt.",
        cta: "Ladda upp underlag",
      },
      // TES-720 — the CP-06 document's variant B (2026-09-16): the row shows
      // "Att göra", that Ella is the payee, and what happens after the
      // payment. The price comes from `payment`, never from the text.
      lab_payment: {
        title: "Betala blodprovet",
        // TES-737 said what the price included (Ö-153). TES-1286 (TES-1310:
        // no patient copy on "vad som ingår eller inte ingår i priset") drops
        // that sentence; the rest is unchanged.
        body: "Betalningen görs till Ella i appen. När den är bekräftad låses provtagningsinstruktionerna upp och du kan lämna blodprovet.",
        cta: "Se och betala",
        statusLabels: { done: "Betalt", missing: "Att göra" },
      },
      new_blood_test: {
        title: "Lämna blodprovet",
        body: "Provet behövs som en del av läkarens medicinska bedömning. När resultaten är klara granskas de av din läkare.",
        cta: "Se provtagningsinstruktioner",
        lockedNote: "Låses upp när betalningen av blodprovet är bekräftad.",
        reportCta: "Jag har lämnat blodprovet",
        // TES-942 — Emelie's waiting line (TES-941), verbatim.
        reportedNote:
          "Tack. Vi inväntar provsvaret. Din läkare återkommer med besked och behandlingsbeslut när provsvaret har kommit.",
        undoReportCta: "Ångra markeringen",
      },
      blood_pressure_pulse: {
        // TES-849 — Emelie 2026-09-22 (Ö-203), verbatim. Her document covers
        // the row and the form it opens as one item, and splits in two: the
        // WHY belongs to the row, which is where the patient meets the task,
        // and the HOW to the form, which is where she is about to measure.
        // So her first paragraph lands here and her second in
        // `recordBloodPressure.intro` (apps/mobile sv.json).
        //
        // It replaces TES-735's title and body, whose provenance was unknown.
        // What hers adds is WHERE she may measure — the old body named no
        // place at all, which left "läkaren behöver" sounding like something
        // only a clinic could supply.
        title: "Mät blodtryck och vilopuls",
        // TES-1171 — the first clause made time-neutral, after TES-1169's
        // principle for this CP-06 checklist: the row is now also asked of a
        // patient months into treatment, where "inför ditt recept" is false.
        // Her original: "För att läkaren ska kunna slutföra bedömningen inför
        // ditt recept behöver vi …". Our wording, proposed in TES-1171; the
        // rest of her sentence is unchanged.
        body: "För att läkaren ska kunna fortsätta din medicinska bedömning eller uppföljning behöver vi känna till ditt aktuella blodtryck och din vilopuls. Du kan mäta hemma, på ett apotek eller på en vårdcentral.",
        // Ö-207, ruled by Emelie 2026-09-22. The mismatch flagged above is
        // resolved, and not in either direction we proposed: the row keeps the
        // title "Mät blodtryck och vilopuls" and the opener becomes a short
        // "Fyll i värden". Four words of the title are no longer repeated on
        // the button beneath it, and the verbs stop disagreeing.
        //
        // Her full set, so the next reader does not have to reconstruct it:
        //   checklist row   "Mät blodtryck och vilopuls"   (title, here)
        //   opener          "Fyll i värden"                  (cta, here)
        //   form heading    "Mät blodtryck och vilopuls"
        //   save button     "Spara uppgifter"
        //
        // TES-1310/TES-1313 — the opener is superseded. CP-06 and 09C
        // (2026-10-13) give the row the same two actions as Hem's card, in
        // this order: "Så mäter du blodtryck och vilopuls"
        // (CP06_LAB_ACTION_LABELS.bpGuide), then "Registrera värden".
        cta: "Registrera värden",
      },
    },
  },
  en: {
    // TES-1169 — derived from her Swedish above.
    title: "We need some more information from you",
    body: "The doctor needs more information to be able to continue your medical assessment or follow-up. See and complete the task below.",
    whenDone: {
      label: "When this is done",
      body: "The doctor can move forward with your case.",
    },
    cta: "See and complete the tasks",
    waiting: {
      title: "You have done your part",
      body: "There is nothing more for you to do in Ella right now. You will be told in the app when the next step is ready.",
    },
    labWaiting: {
      title: "Your documentation has been submitted",
      body: "There is nothing more for you to do in Ella right now. We are waiting for the lab results. The doctor will get back to you when all of the documentation can be assessed.",
    },
    transferCompletion: {
      title: "Complete the details about your treatment",
      body: "The doctor needs you to fill in these parts of the details about your current treatment. They are required for the doctor to be able to go ahead.",
      cta: "Complete the details",
    },
    customTask: {
      markDoneCta: "Mark as done",
      answerLabel: "Your answer",
      answerCta: "Send answer",
      answeredOn: "Sent {{date}}",
      updateAppToAnswer:
        "Update the app to the latest version to be able to answer the question.",
      submitCta: "Send to the doctor",
      uploadedNotSubmitted:
        "Uploaded but not sent. The doctor receives it once you have sent it.",
      submitted: "The documentation has been sent to the doctor.",
      homeCta: "Do the task",
      homeUploadCta: "Upload evidence",
    },
    status: { done: "Done", missing: "Missing" },
    tasks: {
      previous_lab_results: {
        title: "Upload previous lab results",
        body: "Upload clear pictures of the lab results you already have. The doctor's assessment continues once the required documentation is complete.",
        cta: "Upload results",
      },
      prescription_evidence: {
        title: "Upload evidence of your current treatment",
        body: "The doctor needs evidence showing your current prescription or order. Take a photo or choose a screenshot where your name, the medication's name, strength and the prescription's date or validity are as clear as possible.",
        cta: "Upload evidence",
      },
      lab_payment: {
        title: "Pay for the blood test",
        body: "The payment is made to Ella in the app. Once it is confirmed, the sampling instructions unlock and you can give the blood sample.",
        cta: "View and pay",
        statusLabels: { done: "Paid", missing: "To do" },
      },
      new_blood_test: {
        title: "Give the blood sample",
        body: "The test is needed as part of the doctor's medical assessment. Once the results are ready, your doctor reviews them.",
        cta: "See the sampling instructions",
        lockedNote: "Unlocks once the blood test payment is confirmed.",
        reportCta: "I have given the blood sample",
        reportedNote:
          "Thank you. We are waiting for the lab results. Your doctor will get back to you with an answer and a treatment decision once the results have arrived.",
        undoReportCta: "Undo",
      },
      // Derived from her Swedish above — ours, like the rest of this block.
      blood_pressure_pulse: {
        title: "Measure your blood pressure and resting pulse",
        body: "So the doctor can continue your medical assessment or follow-up, we need to know your current blood pressure and your resting pulse. You can measure at home, at a pharmacy or at a health centre.",
        // The same as CP06_LAB_COPY.en.bloodPressure.cta.
        cta: "Record readings",
      },
    },
  },
};

// TES-1094 — CP-06's neutral waiting state on the surfaces around the card:
// the "Dina steg" rung (point 6) and Home's greeting (point 4, no longer
// asking for a blood test or measurements). Pick the card's text with
// `medicalEvidenceWaitingCopy`.
//
// TES-1492 (Ö-659, Emelie 2026-10-20) — the waiting rung carries the name of
// the completion step it waits on: "Medicinskt underlag är namnet på kompletteringssteget. […] Använd inte det otydliga ”Medicinsk uppföljning” för detta väntesteg."
export const MEDICAL_EVIDENCE_WAITING_STEP_TITLE: Record<"sv" | "en", string> =
  {
    sv: "Medicinskt underlag",
    en: "Medical documentation",
  };

// TES-1492 (Ö-659) — the waiting rung's body opens with what is actually
// awaited: "Väntestatus visar vad som faktiskt inväntas, exempelvis ”Vi inväntar ditt provsvar” eller ”Läkaren granskar ditt underlag”. […] Status väljs utifrån verifierad händelse; patientens självrapport om lämnat prov är inte bevis för att svaret kommit eller granskats."
// `status` is one of her two examples verbatim; the sentences after it are
// the approved waiting texts' (`labWaiting`, CP06_LAB_COPY waiting.inReview).
// `resultAwaited` while no result is registered on the case's order (a
// sample reported on her word included); `inReview` once a doctor has
// registered it. English ours.
export const MEDICAL_EVIDENCE_WAITING_STEP_STATUS: Record<
  "sv" | "en",
  Record<"resultAwaited" | "inReview", { status: string; body: string }>
> = {
  sv: {
    resultAwaited: {
      status: "Vi inväntar ditt provsvar",
      body: "Vi inväntar ditt provsvar. Du behöver inte göra något mer i Ella just nu. Läkaren återkommer när hela underlaget kan bedömas.",
    },
    inReview: {
      status: "Läkaren granskar ditt underlag",
      body: "Läkaren granskar ditt underlag. Du får besked i appen när bedömningen är klar.",
    },
  },
  en: {
    resultAwaited: {
      status: "We are waiting for your lab results",
      body: "We are waiting for your lab results. There is nothing more for you to do in Ella right now. The doctor will get back to you when all of the documentation can be assessed.",
    },
    inReview: {
      status: "The doctor is reviewing your documentation",
      body: "The doctor is reviewing your documentation. You will be told in the app when the assessment is done.",
    },
  },
};

// The lab variant when the case includes a new blood test (the result is what
// is awaited), Ö-136's general wait otherwise.
export function medicalEvidenceWaitingCopy(
  locale: "sv" | "en",
  hasBloodTest: boolean,
): { title: string; body: string } {
  const copy = MEDICAL_EVIDENCE_COPY[locale];
  return hasBloodTest ? copy.labWaiting : copy.waiting;
}

export const MEDICAL_EVIDENCE_DEEP_LINK = "carebyella://medical-evidence";

// TES-1635 (TES-1632, Emelie 2026-10-28) — every komplettering is its own
// "Att göra" card on Hem, and its CTA leads straight to that item: the
// checklist opened on one row. `ref` is the row's task key
// (`previous_lab_results`, `prescription_evidence`) or the id of the
// doctor's own row. Built in code, never authored, so it is deliberately not
// in the authored-content grammar (deep-links.ts). Sent only to a build that
// claims CLIENT_CONTRACT_CHANGES.completionItemLink; an older one ignores an
// unknown path, so it is sent the checklist itself.
const MEDICAL_EVIDENCE_ITEM_PREFIX = `${MEDICAL_EVIDENCE_DEEP_LINK}/item/`;

export function medicalEvidenceItemDeepLink(ref: string): string {
  return `${MEDICAL_EVIDENCE_ITEM_PREFIX}${encodeURIComponent(ref)}`;
}

// The row a checklist-item link names, or null when `path` is not one.
export function medicalEvidenceItemOf(path: string): string | null {
  if (!path.toLowerCase().startsWith(MEDICAL_EVIDENCE_ITEM_PREFIX)) return null;
  const ref = path.slice(MEDICAL_EVIDENCE_ITEM_PREFIX.length);
  if (!/^[A-Za-z0-9_%-]+$/.test(ref)) return null;
  try {
    return decodeURIComponent(ref);
  } catch {
    return null;
  }
}

// TES-1285 (TES-1269 points 1, 2, 3, 5, 8, 9) — the blood test and the
// blood pressure/pulse of one CP-06 case as separate Hem tasks, the greeting
// per partial state, and the "Medicinskt underlag" rung. Swedish verbatim from
// CP-06/09C ("KANONISKT BESLUT 2026-10-12 — SEPARATA PATIENTUPPGIFTER …" §3,
// §6, §7, §8 and "HEMSKÄRMSLÄGEN — DELSTATUS …" state 1–7). The English is
// ours. `themeLabel` is the rung title in eyebrow capitals; Home also uses
// it while CP-06 waits (TES-1492, Ö-659).
export interface Cp06LabCopy {
  bloodTest: {
    title: string;
    unpaidBody: string;
    unpaidCta: string;
    paidBody: string;
    // 2.0.8 shows one CTA per card: the first of CP-06's three
    // ("Hitta provtagningsställe", "Så förbereder du dig",
    // "Jag har lämnat blodprovet"), opening the checklist where all three are.
    paidCta: string;
    reportedTitle: string;
    reportedBody: string;
  };
  bloodPressure: { title: string; body: string; cta: string };
  // §1 while tasks remain. `body` null: state 1 is a single sentence.
  greeting: {
    bothOpen: { title: string; body: string | null };
    sampleGiven: { title: string; body: string };
    valuesRegistered: { title: string; body: string };
  };
  // Every patient task done (card, greeting and the waiting rung).
  waiting: {
    // State 4 — sample reported, result awaited.
    submitted: { title: string; body: string };
    // State 5 — a result is registered, the doctor's assessment remains.
    inReview: { title: string; body: string };
    // State 7 — only blood pressure and pulse were asked for.
    valuesOnly: { title: string; body: string };
  };
  step: { title: string; body: string };
  themeLabel: string;
}

export const CP06_LAB_COPY: Record<Locale, Cp06LabCopy> = {
  sv: {
    bloodTest: {
      title: "Lämna ditt blodprov",
      unpaidBody:
        "För att läkaren ska kunna slutföra din medicinska bedömning behöver du lämna blodprov.",
      unpaidCta: "Betala blodprovet",
      paidBody:
        "För att läkaren ska kunna slutföra din medicinska bedömning behöver du lämna blodprov. Börja med att hitta ett provtagningsställe och läs hur du förbereder dig. När provet är lämnat bekräftar du det här.",
      paidCta: "Hitta provtagningsställe",
      reportedTitle: "Blodprovet är lämnat",
      reportedBody:
        "Vi inväntar provsvaret. Du behöver inte göra något mer med blodprovet just nu.",
    },
    bloodPressure: {
      title: "Registrera blodtryck och vilopuls",
      body: "Läkaren behöver aktuella värden för att kunna slutföra din medicinska bedömning.",
      cta: "Registrera värden",
    },
    greeting: {
      bothOpen: {
        title:
          "För att läkaren ska kunna slutföra din medicinska bedömning behöver du lämna blodprov och registrera blodtryck och vilopuls.",
        body: null,
      },
      sampleGiven: {
        title: "Ditt blodprov är lämnat",
        body: "Vi inväntar provsvaret. Nu behöver du registrera blodtryck och vilopuls.",
      },
      valuesRegistered: {
        title: "Dina värden är registrerade",
        body: "Nu återstår att lämna blodprovet.",
      },
    },
    waiting: {
      submitted: {
        title: "Ditt underlag är inskickat",
        body: "Du behöver inte göra något mer i Ella just nu. Vi inväntar provsvaret och läkaren återkommer när hela underlaget kan bedömas.",
      },
      inReview: {
        title: "Läkaren går igenom ditt underlag",
        body: "Ditt blodprov och dina registrerade värden finns nu hos läkaren. Du får besked i appen när bedömningen är klar.",
      },
      valuesOnly: {
        title: "Ditt underlag är inskickat",
        body: "Läkaren återkommer när det har bedömts.",
      },
    },
    step: {
      title: "Medicinskt underlag",
      body: "Lämna blodprov och registrera blodtryck och vilopuls så att läkaren kan slutföra sin bedömning.",
    },
    themeLabel: "MEDICINSKT UNDERLAG",
  },
  en: {
    bloodTest: {
      title: "Give your blood sample",
      unpaidBody:
        "So the doctor can complete your medical assessment, you need to give a blood sample.",
      unpaidCta: "Pay for the blood test",
      paidBody:
        "So the doctor can complete your medical assessment, you need to give a blood sample. Start by finding a sampling site and reading how to prepare. Once the sample is given, confirm it here.",
      paidCta: "Find a sampling site",
      reportedTitle: "The blood sample is given",
      reportedBody:
        "We are waiting for the lab results. There is nothing more for you to do with the blood test right now.",
    },
    bloodPressure: {
      title: "Record blood pressure and resting pulse",
      body: "The doctor needs current readings to be able to complete your medical assessment.",
      cta: "Record readings",
    },
    greeting: {
      bothOpen: {
        title:
          "So the doctor can complete your medical assessment, you need to give a blood sample and record your blood pressure and resting pulse.",
        body: null,
      },
      sampleGiven: {
        title: "Your blood sample is given",
        body: "We are waiting for the lab results. Now you need to record your blood pressure and resting pulse.",
      },
      valuesRegistered: {
        title: "Your readings are recorded",
        body: "What remains is to give the blood sample.",
      },
    },
    waiting: {
      submitted: {
        title: "Your documentation has been submitted",
        body: "There is nothing more for you to do in Ella right now. We are waiting for the lab results, and the doctor will get back to you when all of the documentation can be assessed.",
      },
      inReview: {
        title: "The doctor is reviewing your documentation",
        body: "Your blood test and your recorded readings are now with the doctor. You will be told in the app when the assessment is done.",
      },
      valuesOnly: {
        title: "Your documentation has been submitted",
        body: "The doctor will get back to you once it has been assessed.",
      },
    },
    step: {
      title: "Medical documentation",
      body: "Give a blood sample and record your blood pressure and resting pulse so the doctor can complete the assessment.",
    },
    themeLabel: "MEDICAL DOCUMENTATION",
  },
};

// TES-1286 — where the actions on Hem's CP-06 cards lead (app 2.0.9). Each
// is a deep link every 2.0.9 surface resolves the same way, so Hem and the
// checklist under Din vård open the same instance and the same article.
export const LAB_PAYMENT_DEEP_LINK = "carebyella://lab-payment";
export const FIND_LAB_DEEP_LINK = "carebyella://blood-test";
// TES-1287 — "Om ditt blodprov".
export const ABOUT_BLOOD_TEST_DEEP_LINK = "carebyella://blood-test/about";
// TES-1287/TES-1309 — the one published "Så förbereder du dig".
export const BLOOD_TEST_PREPARATION_ARTICLE_KEY = "blood_test.before_checklist";
export const BLOOD_TEST_PREPARATION_DEEP_LINK = `carebyella://article/${BLOOD_TEST_PREPARATION_ARTICLE_KEY}`;
// The CP-06 blood-pressure form (RecordBloodPressure), opened directly.
export const RECORD_BLOOD_PRESSURE_DEEP_LINK =
  "carebyella://medical-evidence/blood-pressure";
// TES-1313's "Så mäter du blodtryck och vilopuls" page. Its text is the
// "FULLSTÄNDIG PATIENTTEXT" section of CP-06 and 09C (2026-10-14), imported by
// scripts/imports/import-bp-pulse-guide-tes1313.sh. Hem's BP card shows the
// action only while an article with this key is published, so it appears,
// first in the order TES-1310 sets, the day the content does.
export const BP_PULSE_GUIDE_ARTICLE_KEY = "measurements.bp_pulse.how_to";

// TES-1313 — how old a reading saved through the CP-06 form may be. CP-06 and
// 09C: "Du kan registrera värden som du redan har om dom är tagna i vila
// enligt instruktionerna ovan och inte är äldre än sju dagar." Counted in
// calendar days, inclusive: a reading from seven days ago counts, one from
// eight does not. The form offers no earlier day and the server refuses one.
export const CP06_BLOOD_PRESSURE_MAX_AGE_DAYS = 7;

/** The earliest calendar day (YYYY-MM-DD) a CP-06 reading may be dated on,
 * given today's calendar day (YYYY-MM-DD). */
export function cp06BloodPressureEarliestDay(today: string): string {
  const [y, m, d] = today.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d - CP06_BLOOD_PRESSURE_MAX_AGE_DAYS))
    .toISOString()
    .slice(0, 10);
}

// TES-1286 — the action labels on Hem's CP-06 cards, in CP-06/09C's words
// (§3, §6), "Visa provtagningen" and "Så mäter du …" included. The undo is
// the checklist row's own label (TES-737), so both places say the same. The
// English is ours.
export const CP06_LAB_ACTION_LABELS: Record<
  Locale,
  {
    findLab: string;
    prepare: string;
    report: string;
    about: string;
    showSample: string;
    undo: string;
    bpGuide: string;
  }
> = {
  sv: {
    findLab: "Hitta provtagningsställe",
    prepare: "Så förbereder du dig",
    report: "Jag har lämnat blodprovet",
    about: "Om ditt blodprov",
    showSample: "Visa provtagningen",
    undo: "Ångra markeringen",
    bpGuide: "Så mäter du blodtryck och vilopuls",
  },
  en: {
    findLab: "Find a sampling site",
    prepare: "How to prepare",
    report: "I have given the blood sample",
    about: "About your blood test",
    showSample: "View the sampling",
    undo: "Undo",
    bpGuide: "How to measure blood pressure and resting pulse",
  },
};

// TES-735/TES-864 — `MeasurementSession.sourceSurface` for the CP-06 task's
// own blood-pressure form, as opposed to a routine "Dina värden" entry.
//
// Shared rather than duplicated because both ends now depend on the exact
// string: the app stamps it, and the server reads it as the patient saying
// "this reading answers CP-06" and stamps the link to her open case
// (Ö-207 point 4). The app never names the case id — the server resolves it
// from the patient's own checklist, so a client cannot link a reading to
// somebody else's completion request.
export const CP06_BLOOD_PRESSURE_SURFACE = "cp06/blood_pressure_pulse";
